import { useEffect, useState, useRef, useCallback } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { RelatedApps, RELATED_BY_APP } from '@hypneuronerd/ui';
import { CandleSVG } from './components/CandleSVG';
import { ReturnLink } from './components/ReturnLink';
import { Settings } from './components/Settings';
import { useChime } from './hooks/useChime';
import type {
  DurationMinutes,
  Reflection,
  Settings as SettingsType,
  Stats,
} from './data/types';
import { DEFAULT_SETTINGS, DEFAULT_STATS, DURATIONS } from './data/types';
import styles from './App.module.css';

type Phase = 'welcome' | 'burning' | 'completed';

// YYYY-MM-DD in the visitor's local timezone — used as a streak key.
function todayKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Current streak = consecutive days with at least one session, ending
// today or yesterday (a one-day grace so a near-midnight light still
// counts as continuing the streak).
function computeCurrentStreak(dates: string[]): number {
  if (dates.length === 0) return 0;
  const sorted = [...new Set(dates)].sort();
  const latest = sorted[sorted.length - 1]!;
  const oneDayMs = 86_400_000;
  const todayTs = new Date(todayKey()).getTime();
  const gap = (todayTs - new Date(latest).getTime()) / oneDayMs;
  if (gap > 1) return 0;
  const lookup = new Set(sorted);
  let cursor = new Date(latest);
  let streak = 0;
  while (true) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    const key = `${y}-${m}-${d}`;
    if (lookup.has(key)) {
      streak += 1;
      cursor = new Date(cursor.getTime() - oneDayMs);
    } else {
      break;
    }
  }
  return streak;
}

const REFLECTIONS_KEY = 'candle:reflections';
const MAX_REFLECTIONS = 30;

function recordSession(
  durationMin: number,
  prev: Stats,
): Stats {
  const today = todayKey();
  const dates = [...new Set([...prev.sessionDates, today])].sort();
  const currentStreak = computeCurrentStreak(dates);
  return {
    totalSessions: prev.totalSessions + 1,
    totalMinutes: prev.totalMinutes + durationMin,
    lastSession: new Date().toISOString(),
    sessionDates: dates,
    longestStreak: Math.max(prev.longestStreak, currentStreak),
  };
}

export function App() {
  const [settings, setSettings] = useState<SettingsType>(() =>
    storage.get('candle:settings', DEFAULT_SETTINGS)
  );
  const [phase, setPhase] = useState<Phase>('welcome');
  const [duration, setDuration] = useState<DurationMinutes>(settings.defaultDuration);
  const [showRemaining, setShowRemaining] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const burnStartRef = useRef<number | null>(null);
  const holdTimerRef = useRef<number | null>(null);
  const remainingFadeRef = useRef<number | null>(null);
  const chime = useChime();

  // Keep settings in localStorage in sync
  useEffect(() => {
    storage.set('candle:settings', settings);
  }, [settings]);

  // Reflect default-duration changes from settings into the picker when idle
  useEffect(() => {
    if (phase === 'welcome') setDuration(settings.defaultDuration);
  }, [settings.defaultDuration, phase]);

  const light = useCallback(() => {
    // Defensive: kill any stale hold-to-extinguish interval that
    // survived the previous burn. Without this, "another candle" →
    // light the new one → the previous burn's interval keeps
    // running and re-triggers extinguish on the fresh flame.
    if (holdTimerRef.current !== null) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setHoldProgress(0);
    burnStartRef.current = Date.now();
    setPhase('burning');
  }, []);

  const handleBurnComplete = useCallback(() => {
    setPhase('completed');
    chime.play(settings.endSound);
    const stats = storage.get<Stats>('candle:stats', DEFAULT_STATS);
    storage.set<Stats>('candle:stats', recordSession(duration, stats));
  }, [chime, settings.endSound, duration]);

  const reset = useCallback(() => {
    // Same defensive cleanup as in light(). The reset path is hit
    // from the "another candle" button, and we want the next press
    // to start from a clean slate.
    if (holdTimerRef.current !== null) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (remainingFadeRef.current !== null) {
      clearTimeout(remainingFadeRef.current);
      remainingFadeRef.current = null;
    }
    burnStartRef.current = null;
    setPhase('welcome');
    setHoldProgress(0);
    setShowRemaining(false);
  }, []);

  // Tap-and-hold to extinguish early (2 seconds)
  const onPressStart = useCallback(() => {
    if (phase !== 'burning') return;
    const start = Date.now();
    holdTimerRef.current = window.setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(1, elapsed / 2000);
      setHoldProgress(pct);
      if (pct >= 1) {
        clearInterval(holdTimerRef.current ?? undefined);
        holdTimerRef.current = null;
        setHoldProgress(0);
        // record a partial session
        const sessionMin = Math.max(
          1,
          Math.round(((Date.now() - (burnStartRef.current ?? Date.now())) / 60000))
        );
        const stats = storage.get<Stats>('candle:stats', DEFAULT_STATS);
        storage.set<Stats>('candle:stats', recordSession(sessionMin, stats));
        setPhase('completed');
      }
    }, 50);
  }, [phase]);

  const onPressEnd = useCallback(() => {
    if (holdTimerRef.current !== null) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setHoldProgress(0);
  }, []);

  // Tap (without hold) shows remaining time briefly during burn
  const onTap = useCallback(() => {
    if (phase !== 'burning') return;
    setShowRemaining(true);
    if (remainingFadeRef.current !== null) {
      clearTimeout(remainingFadeRef.current);
    }
    remainingFadeRef.current = window.setTimeout(() => setShowRemaining(false), 3000);
  }, [phase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (holdTimerRef.current !== null) clearInterval(holdTimerRef.current);
      if (remainingFadeRef.current !== null) clearTimeout(remainingFadeRef.current);
    };
  }, []);

  // Periodic re-render during burn to update the remaining-time readout
  const [, force] = useState(0);
  useEffect(() => {
    if (phase !== 'burning') return;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const burnMs = duration * 60 * 1000;
  const remainingMs =
    phase === 'burning' && burnStartRef.current
      ? Math.max(0, burnMs - (Date.now() - burnStartRef.current))
      : 0;
  const remainingMin = Math.ceil(remainingMs / 60000);
  // mm:ss readout so the melt is legible at second-resolution. The
  // wax-overlay descent already maps linearly to elapsed time; this
  // line lets the visitor confirm that intuition.
  const remainingSecondsTotal = Math.max(0, Math.ceil(remainingMs / 1000));
  const remainingMmSs = `${Math.floor(remainingSecondsTotal / 60)}:${String(
    remainingSecondsTotal % 60,
  ).padStart(2, '0')}`;

  // Stats are read on every render so the welcome screen reflects the
  // most recent session as soon as one completes. Backfill missing
  // fields so legacy stats blobs from before streak tracking landed
  // don't break the UI.
  const storedStats = storage.get<Partial<Stats>>('candle:stats', {});
  const stats: Stats = { ...DEFAULT_STATS, ...storedStats };
  const currentStreak = computeCurrentStreak(stats.sessionDates);

  // Reflection state — only used on the completed screen. Optional;
  // empty submission is fine, the session is already recorded.
  const [reflectionNote, setReflectionNote] = useState('');
  const [reflectionSaved, setReflectionSaved] = useState(false);

  // Reset reflection state when starting a new session.
  useEffect(() => {
    if (phase === 'welcome') {
      setReflectionNote('');
      setReflectionSaved(false);
    }
  }, [phase]);

  const saveReflection = useCallback(() => {
    const note = reflectionNote.trim();
    if (!note) return;
    const list = storage.get<Reflection[]>(REFLECTIONS_KEY, []);
    const entry: Reflection = {
      date: new Date().toISOString(),
      durationMinutes: duration,
      note,
    };
    const next = [entry, ...list].slice(0, MAX_REFLECTIONS);
    storage.set(REFLECTIONS_KEY, next);
    setReflectionSaved(true);
  }, [reflectionNote, duration]);

  return (
    <>
      <AgeGateOverlay />
      <main
        className={styles.stage}
        onPointerDown={onPressStart}
        onPointerUp={onPressEnd}
        onPointerLeave={onPressEnd}
        onClick={onTap}
      >
        <ReturnLink hidden={phase === 'burning'} />
        <Settings settings={settings} onChange={setSettings} />

        <div className={styles.candleWrap}>
          <CandleSVG
            state={phase === 'burning' ? 'burning' : phase === 'completed' ? 'finished' : 'idle'}
            burnDurationMs={burnMs}
            onBurnComplete={handleBurnComplete}
            onLight={light}
          />

          {phase === 'welcome' && (
            <div className={styles.welcomeText}>
              <p className={styles.label}>{duration} minutes of stillness</p>
              <div className={styles.durations} role="radiogroup" aria-label="duration">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    role="radio"
                    aria-checked={duration === d}
                    className={`${styles.dot} ${duration === d ? styles.dotActive : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDuration(d);
                    }}
                  >
                    {d}
                  </button>
                ))}
              </div>
              <p className={styles.hint}>tap the candle to begin</p>
              {stats.totalSessions > 0 && (
                <>
                  <p className={styles.stats}>
                    {stats.totalSessions} candle{stats.totalSessions === 1 ? '' : 's'} lit ·{' '}
                    {stats.totalMinutes} {stats.totalMinutes === 1 ? 'minute' : 'minutes'} held
                  </p>
                  {currentStreak > 1 && (
                    <p className={styles.streak}>
                      {currentStreak}-day streak
                      {stats.longestStreak > currentStreak && (
                        <span className={styles.streakBest}> · best {stats.longestStreak}</span>
                      )}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {phase === 'burning' && (
            <>
              {/* Always-visible second-resolution timer so the visitor
               * can see the melt against the clock. Sits faint and low
               * so it doesn't pull attention from the flame itself. */}
              <p className={styles.timer}>{remainingMmSs}</p>
              {showRemaining && (
                <p className={styles.remaining}>
                  {remainingMin} {remainingMin === 1 ? 'minute' : 'minutes'} remain
                </p>
              )}
              {holdProgress > 0 && (
                <div className={styles.holdHint}>
                  <span>extinguishing…</span>
                  <div className={styles.holdBar}>
                    <div
                      className={styles.holdFill}
                      style={{ transform: `scaleX(${holdProgress})` }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {phase === 'completed' && (
            <div className={styles.completedText}>
              <p className={styles.label}>well done</p>

              {/* Optional one-line reflection. The session is already
               * recorded; this is just a private note about why the
               * visitor sat today. Saved locally, never uploaded. */}
              {!reflectionSaved ? (
                <form
                  className={styles.reflectionForm}
                  onClick={(e) => e.stopPropagation()}
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveReflection();
                  }}
                >
                  <label htmlFor="candle-reflection" className={styles.reflectionLabel}>
                    why did you sit today?
                  </label>
                  <textarea
                    id="candle-reflection"
                    className={styles.reflectionInput}
                    value={reflectionNote}
                    onChange={(e) => setReflectionNote(e.target.value)}
                    placeholder="one line is enough. or skip."
                    maxLength={240}
                    rows={2}
                  />
                  <div className={styles.reflectionActions}>
                    <button
                      type="submit"
                      className={styles.reflectionSave}
                      disabled={!reflectionNote.trim()}
                    >
                      keep
                    </button>
                  </div>
                </form>
              ) : (
                <p className={styles.reflectionSaved}>kept. only you can see it.</p>
              )}

              <button type="button" className={styles.again} onClick={reset}>
                another candle
              </button>

              <RelatedApps apps={RELATED_BY_APP['candle']} surface="midnight" />
            </div>
          )}
        </div>
      </main>
    </>
  );
}
